/**
 * Settings Management Utility
 * Stores settings in localStorage for now (until database is connected)
 */

import { settingsApi, MAIN_URL } from "./api";

export interface SMTPSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  /** Only filled while typing a new password; blank keeps the saved one */
  pass: string;
  /** The server has a password saved (it never sends the password itself) */
  hasPassword?: boolean;
  fromName: string;
  fromEmail: string;
  enabled: boolean;
}

export interface GeneralSettings {
  language: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  taxRate: string;
  quotationValidityDays: string;
  testingMode: boolean;
  quickLoginEmail: string;
  quickLoginPassword: string;
  /** Admin password required to edit a recorded client payment. Write-only from the UI. */
  paymentEditPassword: string;
}

export interface BusinessInfo {
  businessName: string;
  tagline: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  gstNumber: string;
  licenseNumber: string;
}

export interface ThemeSettings {
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
}

export interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  enquiryAlerts: boolean;
  paymentAlerts: boolean;
  taskReminders: boolean;
  dailySummary: boolean;
}

export interface PWASettings {
  appName: string;
  shortName: string;
  description: string;
  icon192: string;
  icon512: string;
  installPromptEnabled: boolean;
}

export interface AppSettings {
  smtp: SMTPSettings;
  general: GeneralSettings;
  business: BusinessInfo;
  theme: ThemeSettings;
  notifications: NotificationSettings;
  pwa: PWASettings;
  logoUrl: string;
  faviconUrl: string;
}

const SETTINGS_KEY = "app_settings";

/**
 * Passwords must never sit in browser storage, where any script or anyone using the
 * computer can read them. Everything cached locally goes through this first.
 */
export function withoutSecrets<T>(settings: T): T {
  const copy = JSON.parse(JSON.stringify(settings ?? {}));
  if (copy.smtp) copy.smtp.pass = "";
  if (copy.general) {
    copy.general.quickLoginPassword = "";
    copy.general.paymentEditPassword = "";
  }
  return copy;
}

function cacheSettings(settings: AppSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(withoutSecrets(settings)));
  } catch {
    // storage full or blocked: the backend copy is the source of truth anyway
  }
}

const defaultSettings: AppSettings = {
  smtp: {
    host: "",
    port: 587,
    secure: false,
    user: "",
    pass: "",
    fromName: "Shalimar Banquet Hall",
    fromEmail: "",
    enabled: false,
  },
  general: {
    language: "en",
    timezone: "Asia/Kolkata",
    dateFormat: "DD/MM/YYYY",
    currency: "INR",
    taxRate: "18",
    quotationValidityDays: "7",
    testingMode: false,
    quickLoginEmail: "",
    quickLoginPassword: "",
    paymentEditPassword: "",
  },
  business: {
    businessName: "Shalimar Banquet Hall",
    tagline: "Premium Event Management ERP",
    address: "123 Marine Drive, Mumbai, Maharashtra 400001",
    phone: "+91 98765 43210",
    email: "info@shalimar.in",
    website: "www.shalimar.in",
    gstNumber: "27AABCU9603R1ZX",
    licenseNumber: "BHT-2026-001",
  },
  theme: {
    primaryColor: "#d4af37",
    secondaryColor: "#2d2d2d",
    fontFamily: "Inter",
  },
  notifications: {
    emailNotifications: true,
    smsNotifications: false,
    enquiryAlerts: true,
    paymentAlerts: true,
    taskReminders: true,
    dailySummary: false,
  },
  pwa: {
    appName: "Shalimar ERP",
    shortName: "Shalimar",
    description: "Premium Event Management ERP for banquet halls and event venues",
    icon192: "",
    icon512: "",
    installPromptEnabled: true,
  },
  logoUrl: "",
  faviconUrl: "",
};

/**
 * Get all settings from localStorage
 */
export function getSettings(): AppSettings {
  if (typeof window === "undefined") return defaultSettings;
  
  const stored = localStorage.getItem(SETTINGS_KEY);
  if (!stored) return defaultSettings;
  
  try {
    const parsed = JSON.parse(stored);
    const hadSecrets = Boolean(parsed?.smtp?.pass || parsed?.general?.quickLoginPassword || parsed?.general?.paymentEditPassword);
    const settings = { ...defaultSettings, ...parsed } as AppSettings;
    if (hadSecrets) cacheSettings(settings); // clean up what older versions stored
    return withoutSecrets(settings);
  } catch {
    return defaultSettings;
  }
}

/**
 * Save settings to localStorage
 */
export function saveSettings(settings: Partial<AppSettings>): void {
  if (typeof window === "undefined") return;
  
  const current = getSettings();
  const updated = { ...current, ...settings };
  cacheSettings(updated);
}

/**
 * Get SMTP settings specifically
 */
export function getSMTPSettings(): SMTPSettings {
  return getSettings().smtp;
}

/**
 * Save SMTP settings
 */
export function saveSMTPSettings(smtp: Partial<SMTPSettings>): void {
  const current = getSettings();
  saveSettings({
    smtp: { ...current.smtp, ...smtp },
  });
}

/**
 * Test SMTP connection using new email settings API
 */
export async function testSMTPConnection(testEmail: string): Promise<{ success: boolean; message: string }> {
  try {
const response = await fetch(`${MAIN_URL}/api/settings/email/test`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Accept": "application/json",
        "Authorization": `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify({
        test_email: testEmail,
      }),
    });

    const result = await response.json().catch((e) => {
      console.error('JSON parse error:', e);
      return null;
    });
    
    if (!result) {
      return { success: false, message: "Invalid response from server" };
    }
    
    return { 
      success: result.success, 
      message: result.message || (result.success ? "Test email sent!" : "Failed to send test email")
    };
  } catch (error: any) {
    console.error('testSMTPConnection error:', error);
    return { success: false, message: error.message || "Connection failed" };
  }
}

/**
 * Save SMTP settings using new email settings API
 */
export async function saveSMTPSettingsToBackend(smtp: Partial<SMTPSettings>): Promise<boolean> {
  try {
    const response = await fetch(`${MAIN_URL}/api/settings/email`, {
      method: "PUT",
      headers: { 
        "Content-Type": "application/json",
        "Accept": "application/json",
        "Authorization": `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify({
        enabled: smtp.enabled,
        host: smtp.host,
        port: smtp.port,
        secure: smtp.secure,
        username: smtp.user,
        password: smtp.pass,
        from_name: smtp.fromName,
        from_email: smtp.fromEmail,
      }),
    });

    const result = await response.json().catch(() => null);
    return result?.success ?? false;
  } catch (error) {
    console.error("Failed to save SMTP settings:", error);
    throw error;
  }
}

/**
 * Load SMTP settings from backend
 */
export async function loadSMTPSettingsFromBackend(): Promise<SMTPSettings | null> {
  try {
    const response = await fetch(`${MAIN_URL}/api/settings/email`, {
      headers: {
        "Accept": "application/json",
        "Authorization": `Bearer ${localStorage.getItem('token') || ''}`
      }
    });
    const result = await response.json().catch((e) => {
      console.error('JSON parse error in loadSMTPSettingsFromBackend:', e);
      return null;
    });
    
    if (!result?.success || !result?.data) {
      console.error('No result from loadSMTPSettingsFromBackend');
      return null;
    }
    
    const data = result.data;
    return {
      enabled: data.enabled,
      host: data.host || '',
      port: data.port || 587,
      secure: data.secure,
      user: data.username || '',
      pass: '', // never sent by the server; leave blank to keep the saved password
      hasPassword: Boolean(data.has_password),
      fromName: data.from_name || 'Shalimar Banquet Hall',
      fromEmail: data.from_email || '',
    };
  } catch (error) {
    console.error("Failed to load SMTP settings:", error);
    return null;
  }
}

/**
 * Save all settings to backend API
 */
export async function syncSettingsToBackend(settings: Partial<AppSettings>): Promise<boolean> {
  try {
    let success = true;

    // Save PWA settings
    if (settings.pwa || settings.logoUrl || settings.faviconUrl) {
      const pwaData = {
        app_name: settings.pwa?.appName,
        short_name: settings.pwa?.shortName,
        description: settings.pwa?.description,
        icon_192: settings.pwa?.icon192,
        icon_512: settings.pwa?.icon512,
        main_logo: settings.logoUrl,
        favicon: settings.faviconUrl,
        install_prompt_enabled: settings.pwa?.installPromptEnabled,
      };

      const response = await fetch(`${MAIN_URL}/api/settings/pwa`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify(pwaData),
      });

      const result = await response.json().catch(() => null);
      success = success && (result?.success ?? false);
    }

    // Save Email settings
    if (settings.smtp) {
      const emailData = {
        enabled: settings.smtp.enabled,
        host: settings.smtp.host,
        port: settings.smtp.port,
        secure: settings.smtp.secure,
        username: settings.smtp.user,
        password: settings.smtp.pass,
        from_name: settings.smtp.fromName,
        from_email: settings.smtp.fromEmail,
      };

      const response = await fetch(`${MAIN_URL}/api/settings/email`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify(emailData),
      });

      const result = await response.json().catch(() => null);
      success = success && (result?.success ?? false);
    }

    // Save General settings
    if (settings.general) {
      const generalData = {
        language: settings.general.language,
        timezone: settings.general.timezone,
        date_format: settings.general.dateFormat,
        currency: settings.general.currency,
        tax_rate: settings.general.taxRate,
        quotation_validity_days: settings.general.quotationValidityDays,
        testing_mode: settings.general.testingMode,
        quick_login_email: settings.general.quickLoginEmail,
        quick_login_password: settings.general.quickLoginPassword,
        // Write-only: only send when the admin actually typed a new value,
        // so a blank field never wipes the stored password.
        ...(settings.general.paymentEditPassword
          ? { payment_edit_password: settings.general.paymentEditPassword }
          : {}),
      };

      const response = await fetch(`${MAIN_URL}/api/settings/general`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify(generalData),
      });

      const result = await response.json().catch(() => null);
      success = success && (result?.success ?? false);
    }

    // Save Business settings
    if (settings.business) {
      const businessData = {
        business_name: settings.business.businessName,
        tagline: settings.business.tagline,
        address: settings.business.address,
        phone: settings.business.phone,
        email: settings.business.email,
        website: settings.business.website,
        gst_number: settings.business.gstNumber,
        license_number: settings.business.licenseNumber,
      };

      const response = await fetch(`${MAIN_URL}/api/settings/business`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify(businessData),
      });

      const result = await response.json().catch(() => null);
      success = success && (result?.success ?? false);
    }

    return success;
  } catch (error) {
    console.error("Failed to sync settings to backend:", error);
    throw error;
  }
}

/**
 * Load settings from backend API
 */
export async function loadSettingsFromBackend(): Promise<AppSettings | null> {
  try {
    const response = await fetch(`${MAIN_URL}/api/settings`, {
      headers: {
        "Accept": "application/json",
        "Authorization": `Bearer ${localStorage.getItem('token') || ''}`
      }
    });

    if (!response.ok) {
      console.error('Failed to load settings:', response.status);
      return null;
    }

    const result = await response.json().catch(() => null);
    
    if (!result?.success || !result?.data) {
      console.error('Invalid settings response');
      return null;
    }

    const data = result.data;
    
    // Start with defaults
    const merged: AppSettings = JSON.parse(JSON.stringify(defaultSettings));

    // Map new structured API response to AppSettings
    if (data.pwa) {
      merged.pwa = {
        appName: data.pwa.app_name || 'Shalimar ERP',
        shortName: data.pwa.short_name || 'Shalimar',
        description: data.pwa.description || '',
        icon192: data.pwa.icon_192 || '',
        icon512: data.pwa.icon_512 || '',
        installPromptEnabled: data.pwa.install_prompt_enabled ?? true,
      };
      merged.logoUrl = data.pwa.main_logo || '';
      merged.faviconUrl = data.pwa.favicon || '';
    }

    if (data.email) {
      merged.smtp = {
        enabled: data.email.enabled ?? false,
        host: data.email.host || '',
        port: data.email.port || 587,
        secure: data.email.secure ?? false,
        user: data.email.username || '',
        pass: '',
        hasPassword: Boolean(data.email.has_password),
        fromName: data.email.from_name || 'Shalimar Banquet Hall',
        fromEmail: data.email.from_email || '',
      };
    }

    if (data.general) {
      merged.general = {
        language: data.general.language || 'en',
        timezone: data.general.timezone || 'Asia/Kolkata',
        dateFormat: data.general.date_format || 'DD/MM/YYYY',
        currency: data.general.currency || 'INR',
        taxRate: data.general.tax_rate || '18',
        quotationValidityDays: data.general.quotation_validity_days || '7',
        testingMode: data.general.testing_mode ?? false,
        quickLoginEmail: data.general.quick_login_email || '',
        quickLoginPassword: data.general.quick_login_password || '',
        // Never read the admin payment password back into the UI (write-only).
        paymentEditPassword: '',
      };
    }

    if (data.business) {
      merged.business = {
        businessName: data.business.business_name || 'Shalimar Banquet Hall',
        tagline: data.business.tagline || 'Premium Event Management ERP',
        address: data.business.address || '',
        phone: data.business.phone || '',
        email: data.business.email || '',
        website: data.business.website || '',
        gstNumber: data.business.gst_number || '',
        licenseNumber: data.business.license_number || '',
      };
    }

    // Cache for instant display next time (without any passwords)
    cacheSettings(merged);

    return merged;
  } catch (error) {
    console.error("Failed to load settings from backend:", error);
    return null;
  }
}

/**
 * Sync settings by key
 */
export async function syncSettingsByKey(
  key: keyof AppSettings, 
  data: unknown
): Promise<boolean> {
  try {
    await settingsApi.updateByKey(key, data);
    
    // Update localStorage
    const current = getSettings();
    cacheSettings({ ...current, [key]: data } as AppSettings);

    return true;
  } catch (error) {
    console.error(`Failed to sync ${key} settings:`, error);
    return false;
  }
}
